import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="mb-12 border-b border-neutral-200 pb-8">
        <div className="label-eyebrow mb-3">Legal</div>
        <h1 className="font-display font-medium text-4xl sm:text-5xl tracking-tight">Terms of Service & Liability Waiver</h1>
        <div className="mt-4 inline-block border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-1.5 rounded-sm">
          DRAFT · Prepared for attorney review · Not yet in effect · v1.0
        </div>
      </div>

      <div className="prose prose-neutral max-w-none text-[15px] leading-relaxed">
        <p className="italic text-neutral-600 border-l-2 border-neutral-300 pl-4">
          <b>IMPORTANT:</b> This is a starting draft, not a finished legal document. Laws on liability
          waivers, home-based business use, and marketplace platform liability vary by state and change
          over time. Have a licensed attorney in Minnesota review and finalize this before it is presented
          to any real host or guest, and before any real booking or payment occurs on the platform.
        </p>

        <Section n="1" title="Who This Agreement Is Between">
          <p>These Terms of Service ("Terms") are an agreement between you ("User," "Host," or "Guest") and
          DenShare LLC ("DenShare," "we," "us"). By creating an account, listing a workspace, or booking a
          workspace through DenShare, you agree to these Terms.</p>
        </Section>

        <Section n="2" title="What DenShare Is — and Is Not">
          <p>DenShare operates an online platform that connects individuals who wish to rent out a private
          workspace ("Hosts") with individuals who wish to book a workspace to work in ("Guests").</p>
          <ul>
            <li>DenShare is not a party to any rental arrangement between a Host and a Guest.</li>
            <li>DenShare does not own, control, manage, or inspect any listed workspace.</li>
            <li>DenShare does not employ Hosts, and Hosts are not agents, employees, or representatives of DenShare.</li>
            <li>DenShare does not guarantee the accuracy of any listing, the condition of any workspace, or the conduct of any Host or Guest.</li>
          </ul>
        </Section>

        <Section n="3" title="Host Responsibilities">
          <p>A Host who lists a workspace on DenShare agrees to:</p>
          <ul>
            <li>Accurately describe the workspace, equipment, and amenities offered, and keep listings up to date.</li>
            <li>Maintain the listed space in a reasonably safe condition, free of known hazards.</li>
            <li>Carry appropriate insurance covering business use of their home, including liability coverage for injury or property damage occurring during a booking (see Section 6).</li>
            <li>Comply with all applicable local laws, homeowners association rules, lease terms, and zoning or home-business regulations that apply to their property.</li>
            <li>Not discriminate against Guests on any basis prohibited by applicable law.</li>
          </ul>
        </Section>

        <Section n="4" title="Guest Responsibilities">
          <p>A Guest who books a workspace on DenShare agrees to:</p>
          <ul>
            <li>Use the workspace, equipment, and any provided software or accounts (including any AI tools or licensed software made available by the Host) only for lawful purposes.</li>
            <li>Treat the Host's property, equipment, and home with reasonable care, and report any damage immediately.</li>
            <li>Not exceed the booked time period without the Host's consent.</li>
            <li>Understand that a workspace is a private residence, and behave accordingly.</li>
          </ul>
        </Section>

        <Section n="5" title="Assumption of Risk & Release of Liability">
          <p>Workspaces listed on DenShare are private homes and private equipment, not commercial office
          space. By using DenShare, you acknowledge and agree to the following:</p>
          <ul>
            <li>Guests use a Host's workspace, desk, computer, and any other equipment or software entirely at their own risk.</li>
            <li>Hosts allow Guests into their home entirely at their own risk.</li>
            <li>To the fullest extent permitted by law, each User releases DenShare LLC, its owners, and its representatives from any and all claims, damages, injuries, or losses arising from or related to a booking, including but not limited to bodily injury, property damage, theft, data loss, or loss of business, EXCEPT where such claims arise from DenShare's own gross negligence or willful misconduct.</li>
            <li>Disputes over property damage, injury, theft, or unsatisfactory conditions occurring during a booking are between the Host and Guest directly. DenShare may assist in good faith but is not obligated to mediate, investigate, or compensate either party.</li>
          </ul>
          <p className="italic text-neutral-600">This section is the core liability shield and needs the
          closest legal review — enforceability of liability waivers, and what can and cannot be
          disclaimed, varies significantly by state law.</p>
        </Section>

        <Section n="6" title="Insurance">
          <p>Hosts are strongly encouraged to confirm with their homeowners or renters insurance provider
          whether their policy covers business use of their home, and to obtain a home-sharing endorsement
          or separate business liability coverage if it does not.</p>
          <p>DenShare LLC maintains its own general liability and technology errors & omissions insurance
          covering the operation of the platform itself. This does not extend to cover individual Hosts'
          properties or Guests' bodily injury or property claims against a Host.</p>
          <p>DenShare may require proof of insurance from Hosts before a listing goes live, at DenShare's
          discretion.</p>
        </Section>

        <Section n="7" title="Payments & Fees">
          <ul>
            <li>Guests pay the full listed hourly rate at the time of booking confirmation, processed through DenShare's payment provider (Stripe).</li>
            <li>DenShare deducts a service fee (commission) from each booking before paying out the Host's share.</li>
            <li>Cancellation, refund, and no-show policies will be set out in a separate Cancellation Policy, referenced and incorporated into these Terms.</li>
          </ul>
        </Section>

        <Section n="8" title="Account & Verification">
          <ul>
            <li>DenShare may require identity verification for Hosts and/or Guests before allowing bookings.</li>
            <li>Users are responsible for maintaining the confidentiality of their account credentials.</li>
            <li>DenShare may suspend or terminate any account for violation of these Terms, fraudulent activity, or conduct that endangers another User.</li>
          </ul>
        </Section>

        <Section n="9" title="Limitation of DenShare's Liability">
          <p>To the fullest extent permitted by law, DenShare LLC's total liability to any User for any
          claim arising out of the use of the platform is limited to the greater of (a) the service fees
          paid by that User to DenShare in the twelve (12) months preceding the claim, or (b) one hundred
          dollars ($100). DenShare is not liable for indirect, incidental, consequential, or punitive
          damages.</p>
        </Section>

        <Section n="10" title="Governing Law">
          <p>These Terms are governed by the laws of the State of Minnesota, without regard to
          conflict-of-law principles. Any dispute arising under these Terms will be resolved in the state
          or federal courts located in Minnesota, unless otherwise required by applicable consumer
          protection law.</p>
        </Section>

        <Section n="11" title="Changes to These Terms">
          <p>DenShare may update these Terms from time to time. Continued use of the platform after
          changes take effect constitutes acceptance of the updated Terms.</p>
        </Section>

        <div className="mt-14 pt-8 border-t border-neutral-200">
          <div className="label-eyebrow mb-4">Appendix A · Guest Booking Waiver (Short Form)</div>
          <p className="text-sm text-neutral-500 mb-2">Displayed to a Guest for acknowledgment at the time of booking:</p>
          <blockquote className="border-l-2 border-neutral-900 pl-4 italic text-neutral-800">
            "I understand I am booking a private home workspace, not a commercial office. I accept full
            responsibility for my own safety and any use of the Host's equipment, software, or AI tools
            during my booking, and I release DenShare from liability for any injury, loss, or damage that
            occurs during my visit, except where caused by DenShare's own gross negligence."
          </blockquote>
        </div>

        <div className="mt-10">
          <div className="label-eyebrow mb-4">Appendix B · Host Listing Waiver (Short Form)</div>
          <p className="text-sm text-neutral-500 mb-2">Displayed to a Host for acknowledgment at the time a listing is published:</p>
          <blockquote className="border-l-2 border-neutral-900 pl-4 italic text-neutral-800">
            "I confirm I have the right to offer this space for rental use, that I carry or will obtain
            insurance appropriate for hosting paying guests in my home, and that I release DenShare from
            liability for any injury, loss, theft, or damage arising from a Guest's visit, except where
            caused by DenShare's own gross negligence."
          </blockquote>
        </div>

        <div className="mt-14 pt-6 border-t border-neutral-200 flex items-center justify-between text-sm text-neutral-500">
          <span>Version 1.0 (draft)</span>
          <Link to="/privacy" className="underline text-neutral-900">Privacy Policy →</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display font-medium text-2xl tracking-tight mb-3">{n}. {title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
